import { AppThunk } from "..";
import { api } from "../../services/Api";
import {
  GetScrewPayloadType,
  ScrewsDataTypes,
} from "../../types/ScrewTypes";
import {
  // getActiveScrew,
  // getScrewData,
  getScrewFetchStatus,
  getScrewFilters,
  // getScrewUpdateStatus,
  getUploadedScrewData,
} from "../selectors/ScrewSelectors";
import {
  // setScrewUpdateStatus,
  updateActiveScrew,
  updateAddScrewStatus,
  updateScrewData,
  updateScrewFetchStatus,
  updateScrewUploadStatus,
  updateNewScrewData,
  updateUploadedScrewData,
  updateScrewProperties,
} from "../reducers/ScrewsReducers";

const createUrlParams = (payload: unknown) =>
  payload
    ? "?" +
      Object.entries(payload)
        .filter(([_, value]) => value !== undefined && value !== "undefined" && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join("&")
    : "";

export const getScrewsPropertiesAction = (): AppThunk => (dispatch, getState) => {
  const isScrewFetching = getScrewFetchStatus(getState()) === "loading";

  if (isScrewFetching) return;
  dispatch(updateScrewFetchStatus("loading"));

  api
    .get(`/datasets/screws/properties`)
    .then((screwResponse) => {
      dispatch(updateScrewProperties(screwResponse));
      dispatch(updateScrewFetchStatus("success"));
    })
    .catch(() => {
      dispatch(updateScrewFetchStatus("error"));
      dispatch(updateScrewProperties(null));
    });
};

export const getScrewsAction =
  (payload?: GetScrewPayloadType): AppThunk =>
  async (dispatch, getState) => {
    const isScrewFetching = getScrewFetchStatus(getState()) === "loading";
    if (isScrewFetching) return;

    dispatch(updateScrewFetchStatus("loading"));
    const screwFilters = getScrewFilters(getState());

    const validFilters = Object.keys(screwFilters)
      .filter(
        (key) =>
          screwFilters[key as keyof typeof screwFilters] !== "undefined" && 
          screwFilters[key as keyof typeof screwFilters] !== null
      )
      .map((item) => ({
        [item]: screwFilters[item as keyof typeof screwFilters],
      }))
      .flat()
      .reduce((acc, curr) => Object.assign(acc, curr), {});

    try {
      const screwResponse = await api.get(
        `/datasets/screws/samples${createUrlParams({ ...payload, ...validFilters })}`
      );
      dispatch(updateScrewData(screwResponse));
      dispatch(updateScrewFetchStatus("success"));
      // Only reset to idle after success
      setTimeout(() => {
        dispatch(updateScrewFetchStatus("idle"));
      }, 100);
    } catch (error) {
      console.error("Failed to fetch screws:", error);
      dispatch(updateScrewFetchStatus("error"));
      dispatch(updateScrewData(null));
    }
  };

export const uploadScrewImagesAction =
  (screwData: any): AppThunk =>
  (dispatch, getState) => {
    dispatch(updateScrewUploadStatus("loading"));

    api
      .upload(`/upload-screw-image`, screwData)
      .then((uploadedSamples) => {
        // Store all uploaded samples for multiple screw support
        if (uploadedSamples && uploadedSamples.length > 0) {
          dispatch(updateUploadedScrewData(uploadedSamples));
          dispatch(updateScrewUploadStatus("success"));
        } else {
          dispatch(updateScrewUploadStatus("error"));
          dispatch(updateUploadedScrewData(null));
        }
      })
      .catch((error) => {
        console.error("Failed to upload screw images:", error);
        dispatch(updateScrewUploadStatus("error"));
        dispatch(updateUploadedScrewData(null));
      });
  };

export const addScrewsToDatasetAction =
  (payload: any): AppThunk =>
  (dispatch) => {
    dispatch(updateAddScrewStatus("loading"));

    return api
      .post("/add-screw-to-dataset", payload)
      .then((response) => {
        // Handle the new list response - take the first sample for backward compatibility
        // or store all samples if needed for future use
        if (response && response.length > 0) {
          dispatch(updateNewScrewData(response[0]));
        } else {
          dispatch(updateNewScrewData(null));
        }
        dispatch(updateAddScrewStatus("success"));
      })
      .catch((error) => {
        console.error("Failed to add screws to dataset:", error);
        dispatch(updateAddScrewStatus("error"));
        dispatch(updateNewScrewData(null));
      });
  };

export const createScrewPropertyAction = 
  (payload: { propertyId: string, propertyValue: any }): AppThunk => 
  (dispatch, getState) => {
    const formBody: any = [];
    formBody.push(encodeURIComponent('property_id') + "=" + encodeURIComponent(payload?.propertyId))
    formBody.push(encodeURIComponent('categorical_value') + "=" + encodeURIComponent(payload?.propertyValue))
    api
      .post('/add-categorical-property-value', formBody.join("&"))

      .then((response: any) => {
        dispatch(getScrewsPropertiesAction());
      })
      .catch((error: any) => {
        console.log(error);
      });
};

export const deleteScrewAction = (sampleid: string): AppThunk => (dispatch, getState) => {
  api.delete(`/datasets/screws/samples/${sampleid}`).then(() => {
    dispatch(updateActiveScrew(null));
    dispatch(getScrewsAction()); // Refresh the screws list after deletion
  }).catch((error) => {
    console.error("Failed to delete screw:", error);
  });
};

export const updateExistingScrewAction = (screwData: ScrewsDataTypes): AppThunk => (dispatch, getState) => {
  const { sampleid } = screwData;
  const state = getState();
  const screwProperties = state.ScrewReducer.screwProperties;
  
  if (!screwProperties) {
    console.error("No screw properties found in state");
    return;
  }

  // Create an object mapping property IDs to their values
  const propertyUpdates: { [propertyId: string]: string } = {};
  
  // Map of property names to consider
  const propertyFields = [
    'seating', 'color', 'driver', 'head_diameter', 'thread', 
    'total_length', 'platform', 'brand', 'ref'
  ];

  // For each property in screwData, find its ID from screwProperties and add to updates
  screwProperties.items.forEach(property => {
    const propertyName = property.propertyname.toLowerCase();
    if (propertyFields.includes(propertyName)) {
      const value = screwData[propertyName as keyof ScrewsDataTypes];
      if (value) {
        propertyUpdates[property.propertyid] = value;
      }
    }
  });

  api.put(`/datasets/screws/samples/${sampleid}`, propertyUpdates)
    .then((updatedScrew) => {
      dispatch(updateActiveScrew(updatedScrew));
    })
    .catch((error) => {
      console.error("Failed to update screw:", error);
    });
};

export const getScrewPropertyValuesAction = (sampleId: string): AppThunk => (dispatch) => {
  api
    .get(`/datasets/screws/samples/${sampleId}/propertyvalues`)
    .then((response) => {
      // Transform the response into the format we need
      const propertyValues: Partial<ScrewsDataTypes> = {};
      response.items.forEach((item: any) => {
        propertyValues[item.propertyname as keyof ScrewsDataTypes] = item.categoricalvalue;
      });
      
      // Update the active screw with the new property values
      dispatch(updateActiveScrew((prevScrew: ScrewsDataTypes | null) => ({
        ...prevScrew,
        ...propertyValues
      })));
    })
    .catch((error) => {
      console.error("Failed to fetch screw property values:", error);
    });
};

export const updateScrewPropertyAction = 
  (payload: { propertyId: string, oldValue: string, newValue: string }): AppThunk => 
  (dispatch, getState) => {
    api
      .put(
        `/datasets/screws/properties/${payload.propertyId}/values/${encodeURIComponent(payload.oldValue)}`,
        { new_value: payload.newValue }
      )
      .then(() => {
        dispatch(getScrewsPropertiesAction());
      })
      .catch((error: any) => {
        console.error("Failed to update property value:", error);
      });
  };

export const deleteScrewPropertyValueAction = 
  (payload: { propertyId: string, value: string }): AppThunk => 
  (dispatch, getState) => {
    api
      .delete(`/datasets/screws/properties/${payload.propertyId}/values/${encodeURIComponent(payload.value)}`)
      .then(() => {
        dispatch(getScrewsPropertiesAction());
      })
      .catch((error: any) => {
        console.error("Failed to delete property value:", error);
      });
  };
